import type { MinigameId } from '@minigame-race/contracts';
import { useCallback } from 'react';
import { MINIGAME_COMPONENTS } from './registry.js';

/**
 * Mounts a minigame by id. Contains NO per-game logic — it is a dumb switch
 * driven by the registry, so a new game's UI appears with zero edits here.
 *
 * The only thing it adds is the envelope (`matchId`, `segmentIndex`) around the
 * opaque event the component hands it. It never looks at the event.
 */
export function MinigameHost({
  minigameId,
  matchId,
  segmentIndex,
  view,
  opponentView,
  sendEvent,
  rejectNonce,
  lastReject,
}: {
  minigameId: MinigameId;
  matchId: string;
  segmentIndex: number;
  view: unknown;
  opponentView?: unknown;
  sendEvent: (matchId: string, segmentIndex: number, event: unknown) => void;
  rejectNonce: number;
  lastReject: string | null;
}) {
  const Component = MINIGAME_COMPONENTS[minigameId];

  const emit = useCallback(
    (event: unknown) => sendEvent(matchId, segmentIndex, event),
    [sendEvent, matchId, segmentIndex],
  );

  // `key` forces a fresh component per segment: two Tile Tap segments in one
  // recipe must not share optimistic state.
  return (
    <Component
      key={`${minigameId}:${segmentIndex}`}
      view={view}
      opponentView={opponentView}
      emit={emit}
      rejectNonce={rejectNonce}
      lastReject={lastReject}
    />
  );
}
